import {createApp} from 'vue'
import {i18n, setupPrefs, setupVuetify} from "./components/app/AppSetup"

// highcharts modules
import HighchartsVue from 'highcharts-vue'
import Highcharts from 'highcharts'

// We only need to import the modules, which is strange, but that seems to be how highcharts works
import exportingInit from 'highcharts/modules/exporting'
import exportingDataInit from 'highcharts/modules/export-data'
import offlineExportingInit from 'highcharts/modules/offline-exporting'
import drilldown from 'highcharts/modules/drilldown'
import noDataInit      from 'highcharts/modules/no-data-to-display'
import brokenAxis from 'highcharts/modules/broken-axis'
import highchartsMore from 'highcharts/highcharts-more'

// Needed for startup and auth
import UserSessionService from './services/userSessionService'
import {getHighchartsThemeOptions} from './services/highchartsThemeService'

// Images
import './images/favicon.ico'
import './images/netmet.png'
import './images/netmet_192.png'
import './images/netmet_512.png'
import './images/netmet_maskable_icon.png'
import './images/arrow_dark_gray.png'
import './images/arrow_gray.png'
import './images/arrow_light_gray.png'
import './images/arrow_xl.png'
import './images/arrow_large.png'
import './images/arrow_medium.png'
import './images/arrow_small.png'
import './manifest.webmanifest'
import ChannelPlan from "./services/channelPlan"

if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/service-worker.js')
}

/** This is a default callback called before exporting data, can be overwritten in specific highcharts graph options
 * @param data contains the rows and columns that are being exported
 *
 * TODO:  THIS SHOULD BE SOMEWHERE ELSE
 */
function exportDataCallBack(data) {
    data.dataRows.forEach((row, index) => {
        // index 0 is first row which represents headers, so loop through all the items in it and internationalize each header
        if (index === 0)
        {
            row.forEach((cell, index) => {
                if (row[index] != null)
                {
                    row[index] = i18n.global.t(row[index])
                }
            })
        } else {
            // internationalize only first item in the row which are labels
            row[0] = i18n.global.t(row[0])
        }
    })
}

/**
 * Configure the highcharts library
 * TODO:  THIS SHOULD BE SOMEWHERE ELSE
 */
function configureHighcharts(ncsApp) {
    ncsApp.use(HighchartsVue)

    // Allow onclick attribute in Highcharts HTML legends (used for router navigation links)
    Highcharts.AST.allowedAttributes.push('onclick')

    /***
     noDataInit(Highcharts)
    highchartsMore(Highcharts)
    exportingInit(Highcharts)
    exportingDataInit(Highcharts)
    offlineExportingInit(Highcharts)
    drilldown(Highcharts)
    datajs(Highcharts)
    brokenAxis(Highcharts)
     ***/
    // accessibilityInit(Highcharts)

    // Merge functional defaults (export config, credits, accessibility) with theme options
    // (palette, backgrounds, typography). The theme options come from highchartsThemeService.js
    // which reads the current app theme colors. This replaces the old styledMode approach where
    // charts relied on CSS variables that weren't available in export contexts.
    Highcharts.setOptions(Highcharts.merge({
        accessibility: {enabled: false},
        chart: {
            events: {
                exportData: exportDataCallBack
            }
        },
        credits: {
            enabled: false
        },
        exporting: {
            // This is the path to the libraries used by the offline export - https://www.highcharts.com/docs/export-module/client-side-export
            // The libraries are copied in to the distribution via Webpack
            libURL: '/',
            // default width for all downloading graphs
            sourceWidth: 1500,
            buttons: {
                contextButton: {
                    // menuItems: ["printChart", "separator", "downloadPNG", "downloadJPEG", "downloadPDF", "downloadSVG", "separator", "downloadCSV", "downloadXLS"],
                    menuItems: ['printChart', 'separator', 'downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG', 'separator', 'downloadCSV'],
                    symbol: 'download',
                    symbolStrokeWidth: 1,
                    y: -4
                }
            }
        }
    }, getHighchartsThemeOptions()))

    Highcharts.SVGRenderer.prototype.symbols.download = function (x, y, w, h) {
        return [
            // Arrow stem
            'M', x + w * 0.5, y,
            'L', x + w * 0.5, y + h * 0.7,
            // Arrow head
            'M', x + w * 0.3, y + h * 0.5,
            'L', x + w * 0.5, y + h * 0.7,
            'L', x + w * 0.7, y + h * 0.5,
            // Box
            'M', x, y + h * 0.9,
            'L', x, y + h,
            'L', x + w, y + h,
            'L', x + w, y + h * 0.9
        ]
    }
    // Change the stacked area charts legend symbols to match how the line charts do it and show different symbols in the legend
    Highcharts.seriesTypes.area.prototype.drawLegendSymbol = Highcharts.seriesTypes.line.prototype.drawLegendSymbol
}

async function init()
{
    let ncsApp = null
    if (window.location.href.includes('/offline.html') || window.location.href.includes("/api/logout"))
    {
        return
    }

    try
    {
        console.log ("Loading Bootstrap")
        let bootstrap = await UserSessionService.initialize()
        console.log ("Bootstrap Loaded", bootstrap)

        try
        {
            let userObj = bootstrap.user

            console.log ("setting up prefs")
            await setupPrefs()
            console.log ("prefs setup")

            /**  VUE3 TODO:  IS this needed?
            vuetify.framework.lang.t = ((key, ...params) => {
                return i18n.t(key, params)
            })
             ***/

            // Depends on userPrefs being setup
            console.log ("Configuring setup UserSessionService")
            await UserSessionService.selectDefaultCustomer(userObj)
            console.log ("UserSessionService configured", UserSessionService)

            console.log ("Creating the NCSApp")
            // Dynamically import the NCSApp component must be done after the user session is initialized
            const NCSApp = await import('./NCSApp.vue')
            ncsApp = createApp (NCSApp.default, {version: bootstrap.version, routes: []})
            console.log ("NCSApp Created", ncsApp)

            console.log ("Configuring i18n")
            ncsApp.use (i18n)
            console.log ("i18n configured", i18n)

            console.log ("setting up vuetify")
            const vuetify = setupVuetify()
            ncsApp.use (vuetify)
            console.log ("vuetify setup", vuetify)

            // Must be dynamically imported after the user session is initialized
            const {NCSRouter} = await import('../src/components/app/Routes')
            const router = await NCSRouter;
            ncsApp.use (router)

            console.log ("setup highcharts")
            configureHighcharts(ncsApp)
            console.log ("highcharts setup")

            // Must be called after default customer is set, doesn't matter if it succeeds or not, then load the GUI
            try {
                await ChannelPlan.initChannelPlan()
            } catch {
                console.log("Channel plan is not yet available")
            }

            // User is good and server connection seems to work, so let's bring up the GUI
            // Render the app
            console.log ("mounting ncsApp", ncsApp)
            ncsApp.mount ('#app')
        } catch (error) {
            console.error("conf vue error", error)
        }
    } catch (error) {
        console.error("app init error", error)
    }
}

export default {
    init: async () => {
        await init()
    }
}